/**
 * EB 菜单树 → RuoYi 侧栏路由结构（feat/eb-shell）
 */
import type { RouteLocationNormalized } from 'vue-router';
import {roterPre} from "@/settings";

/** 菜单高亮 / 分栏定位用 path（activeMenu 与 route.path 统一补 /merchant 前缀） */
export function getCrmebRouteLookupPath(route: RouteLocationNormalized): string {
  const raw = (route.meta?.activeMenu as string) || route.path;
  return ensureMerchantPrefix(String(raw));
}

export interface CrmebMenuNode {
  id?: number;
  pid?: number;
  path: string;
  title?: string;
  menuName?: string;
  icon?: string;
  auth?: string[];
  children?: CrmebMenuNode[];
}

/** EB 分栏/侧栏 el-menu 节点（path 含 /merchant 前缀） */
export interface CrmebNavMenuItem {
  path: string;
  title: string;
  icon?: string;
  children?: CrmebNavMenuItem[];
  k?: number;
}

export function crmebNodesToNavList(
  menus: CrmebMenuNode[],
  routePrefix = roterPre,
): CrmebNavMenuItem[] {
  return (menus || [])
    .filter((m) => m.path)
    .map((m) => {
      const path = ensureMerchantPrefix(m.path || '', routePrefix);
      const children = m.children?.length
        ? crmebNodesToNavList(m.children, routePrefix)
        : undefined;
      return {
        path,
        title: m.title || m.menuName || '',
        icon: m.icon || '',
        children: children?.length ? children : undefined,
      };
    });
}

/** 规范路由 path：合并多余斜杠，避免 //merchant/... */
export function normalizeAppPath(path: string): string {
  if (!path) return '/';
  const raw = String(path).split('?')[0].trim();
  if (!raw) return '/';
  const withLeading = raw.startsWith('/') ? raw : `/${raw}`;
  const collapsed = withLeading.replace(/\/+/g, '/');
  return collapsed.length > 1 && collapsed.endsWith('/') ? collapsed.slice(0, -1) : collapsed;
}

/** 规范为带 /merchant 前缀的路由 path（菜单、router-link 统一） */
export function ensureMerchantPrefix(path: string, prefix = roterPre): string {
  if (!path) return prefix || '/';
  const normalized = normalizeAppPath(path.startsWith('/') ? path : `/${path}`);
  if (prefix && !normalized.startsWith(prefix)) {
    return normalizeAppPath(`${prefix}${normalized}`);
  }
  return normalized;
}

/** 页面内 router-link / router.push */
export function crmebPath(path: string, prefix = roterPre): string {
  return ensureMerchantPrefix(path, prefix);
}

export function findFirstMenuUrl(menus: CrmebMenuNode[] | null | undefined): string {
  let url = '/';
  let cur = menus;
  while (cur && cur.length) {
    const menu = cur[0];
    url = ensureMerchantPrefix(menu.path || '/');
    cur = menu.children;
  }
  return url;
}

/** 将 EB 菜单转为现有 Sidebar 组件可用的路由节点 */
export function crmebMenuToSidebarRoutes(
  menus: CrmebMenuNode[],
  routePrefix = roterPre,
): any[] {
  return (menus || []).map((m) => {
      const path = ensureMerchantPrefix(m.path || '', routePrefix);
    const children = m.children?.length
      ? crmebMenuToSidebarRoutes(m.children, routePrefix)
      : undefined;
    const safeName = `CrmebMenu_${m.id ?? path.replace(/[^\w]+/g, '_')}`;
    return {
      path,
      name: safeName,
      meta: {
        title: m.title || m.menuName || '',
        icon: m.icon || '',
      },
      children,
      hidden: false,
    };
  });
}

/** 菜单 auth 与 access 交集（对齐 EB includeArray） */
export function includeArray(list: string[] | undefined, access: string[] | undefined): boolean {
  if (!list?.length) return true;
  if (!access?.length) return false;
  return list.some((item) => access.includes(item));
}

export function filterMenuByAccess(
  menuList: CrmebMenuNode[],
  access: string[],
): CrmebMenuNode[] {
  const result: CrmebMenuNode[] = [];
  (menuList || []).forEach((menu) => {
    const menuAuth = menu.auth;
    if (!menuAuth?.length || includeArray(menuAuth, access)) {
      const node: CrmebMenuNode = { ...menu };
      if (menu.children?.length) {
        node.children = filterMenuByAccess(menu.children, access);
      } else {
        delete node.children;
      }
      result.push(node);
    }
  });
  return result;
}

/** 扁平化侧栏菜单，供顶栏菜单搜索 */
/** 表单子页标题（对齐 EB menu.js getFormTitle） */
export function getCrmebFormPageTitle(route: RouteLocationNormalized): string {
  const segment = route.path.split('/').filter(Boolean).pop() || '';
  switch (segment) {
    case 'mer_base':
      return '店铺配置';
    case 'svip':
      return '付费会员设置';
    default:
      return String(route.meta?.title || '当前页');
  }
}

function flattenMenuTree(menus: CrmebMenuNode[]): CrmebMenuNode[] {
  const list: CrmebMenuNode[] = [];
  const walk = (nodes: CrmebMenuNode[]) => {
    nodes.forEach((node) => {
      list.push(node);
      if (node.children?.length) walk(node.children);
    });
  };
  walk(menus || []);
  return list;
}

/**
 * 菜单树是否包含任一 path 片段（用于混合页跟菜单 A 显隐）
 * needles 如 `/delivery/delivery_point`、`/config/freight/`；兼容有无 /merchant 前缀
 */
export function menuTreeHasPath(
  menus: CrmebMenuNode[] | null | undefined,
  needles: string[],
): boolean {
  if (!menus?.length || !needles?.length) return false;
  const flat = flattenMenuTree(menus);
  const normalizedNeedles = needles
    .map((n) => String(n || '').trim())
    .filter(Boolean)
    .map((n) => {
      const withSlash = n.startsWith('/') ? n : `/${n}`;
      return normalizeAppPath(withSlash).replace(/^\/merchant/, '') || withSlash;
    });
  return flat.some((node) => {
    const raw = String(node.path || '').trim();
    if (!raw) return false;
    const key = normalizeAppPath(raw.startsWith('/') ? raw : `/${raw}`).replace(
      /^\/merchant/,
      '',
    );
    return normalizedNeedles.some(
      (needle) => key === needle || key.startsWith(needle) || key.includes(needle),
    );
  });
}

/** 对照 ti_eb_system_menu：配送门店 / 运费 */
export const MERCHANT_MENU_PATH_LOGISTICS = [
  '/delivery/delivery_point',
  '/config/freight/',
] as const;

/** 对照 ti_eb_system_menu：预约设置 / 预约服务 */
export const MERCHANT_MENU_PATH_RESERVATION = [
  '/product/reservation',
  '/order/reservation',
] as const;

/** 对照 ti_eb_system_menu：商品列表 */
export const MERCHANT_MENU_PATH_RETAIL_PRODUCT = ['/product/list'] as const;

/** 服务人员（预约派单选人） */
export const MERCHANT_MENU_PATH_SERVICE_STAFF = ['/config/service_staff'] as const;

/**
 * 订单「活动类型」筛相关促销：秒杀 / 预售 / 助力 / 拼团
 * （不含优惠券等不改 activityType 的营销）
 */
export const MERCHANT_MENU_PATH_MARKETING_PROMO = [
  '/marketing/seckill',
  '/marketing/presell',
  '/marketing/assist',
  '/marketing/combination',
] as const;

export function merchantMenuShowsLogistics(
  menus: CrmebMenuNode[] | null | undefined,
): boolean {
  return menuTreeHasPath(menus, [...MERCHANT_MENU_PATH_LOGISTICS]);
}

export function merchantMenuShowsReservation(
  menus: CrmebMenuNode[] | null | undefined,
): boolean {
  return menuTreeHasPath(menus, [...MERCHANT_MENU_PATH_RESERVATION]);
}

export function merchantMenuShowsRetailProduct(
  menus: CrmebMenuNode[] | null | undefined,
): boolean {
  return menuTreeHasPath(menus, [...MERCHANT_MENU_PATH_RETAIL_PRODUCT]);
}

export function merchantMenuShowsServiceStaff(
  menus: CrmebMenuNode[] | null | undefined,
): boolean {
  return menuTreeHasPath(menus, [...MERCHANT_MENU_PATH_SERVICE_STAFF]);
}

/** 是否有秒杀/预售/助力/拼团等促销菜单 */
export function merchantMenuShowsMarketingPromo(
  menus: CrmebMenuNode[] | null | undefined,
): boolean {
  return menuTreeHasPath(menus, [...MERCHANT_MENU_PATH_MARKETING_PROMO]);
}

/** 面包屑按 path 去重（保留首次出现顺序，同 path 以后者 title 为准） */
export function dedupeBreadcrumbByPath(
  items: Array<{ path: string; title: string }>,
): Array<{ path: string; title: string }> {
  const pathToItem = new Map<string, { path: string; title: string }>();
  const order: string[] = [];
  for (const item of items) {
    if (!item.title) continue;
    if (!pathToItem.has(item.path)) {
      order.push(item.path);
    }
    pathToItem.set(item.path, item);
  }
  return order.map((path) => pathToItem.get(path)!);
}

/** 按菜单树与当前 path 生成面包屑（优先于 route.matched） */
export function buildMenuBreadcrumbChain(
  menuTree: CrmebMenuNode[],
  targetPath: string,
): Array<{ path: string; title: string }> {
  const flat = flattenMenuTree(menuTree);
  if (!flat.length) return [];
  const normalized = ensureMerchantPrefix(targetPath);
  let current = flat.find((m) => ensureMerchantPrefix(m.path || '') === normalized);
  if (!current) {
    current = flat.find((m) => normalized.startsWith(ensureMerchantPrefix(m.path || '')));
  }
  if (!current) return [];

  const chain: Array<{ path: string; title: string }> = [];
  const byId = new Map<number, CrmebMenuNode>();
  flat.forEach((m) => {
    if (m.id != null) byId.set(m.id, m);
  });

  const visitedIds = new Set<number>();
  let node: CrmebMenuNode | undefined = current;
  while (node) {
    if (node.id != null) {
      if (visitedIds.has(node.id)) break;
      visitedIds.add(node.id);
    }
    chain.unshift({
      path: ensureMerchantPrefix(node.path || ''),
      title: node.title || node.menuName || '',
    });
    if (node.pid == null) break;
    node = byId.get(node.pid);
  }
  return dedupeBreadcrumbByPath(chain);
}

export function flattenCrmebNavMenus(
  menus: CrmebNavMenuItem[],
): Array<{ path: string; title: string; icon?: string }> {
  const list: Array<{ path: string; title: string; icon?: string }> = [];
  const walk = (items: CrmebNavMenuItem[]) => {
    items.forEach((item) => {
      if (item.children?.length) {
        walk(item.children);
      } else if (item.path) {
        list.push({
          path: item.path,
          title: item.title || '',
          icon: item.icon,
        });
      }
    });
  };
  walk(menus || []);
  return list;
}
